import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { request } from '../lib/api';
import { handleScrollRegionKeyDown } from '../lib/scrollRegionKeyboard';
import './customer-analytics-contacts.css';
import { formatDate } from './portalTypes';
import { useSession } from '../session';

type ContactConfiguration = {
  enabled: boolean;
  notifyByEmail: boolean;
  collectName: boolean;
  requireName: boolean;
  collectPhone: boolean;
  collectCompany: boolean;
  collectMessage: boolean;
};

type ConfigurationResponse = {
  platformAvailable: boolean;
  page: { id: string; publicSlug: string | null } | null;
  configuration: ContactConfiguration;
};

type ContactItem = {
  id: string;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  message: string | null;
  preferredMethod: 'EMAIL' | 'PHONE' | 'EITHER' | null;
  acquisitionSource: 'NFC' | 'QR' | 'LINK' | null;
  notes: string | null;
  readAt: string | null;
  source: { routeType: 'qr_card' | 'nfc_card' | 'public_alias' | 'private_link'; publicSlug: string | null; cardDisplayId: string | null };
  consent: { status: string; noticeVersion: string; policyVersion: string; affirmativeAt: string } | null;
  duplicateSignals: { emailMatches: number; phoneMatches: number };
  createdAt: string;
};

type ContactsResponse = {
  unreadCount: number;
  items: ContactItem[];
  nextCursor: string | null;
};

type ContactExport = {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'READY' | 'FAILED' | 'EXPIRED';
  fileName: string;
  rowCount: number | null;
  byteCount: string | null;
  expiresAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

type ContactExportsResponse = { items: ContactExport[] };

const contactInitial = (contact: ContactItem) =>
  (contact.displayName || contact.email || contact.phone || '?').trim().slice(0, 1).toUpperCase() || '?';

const sourceLabel = (source: ContactItem['acquisitionSource']) => {
  if (source === 'QR') return 'QR scan';
  if (source === 'NFC') return 'NFC tap';
  if (source === 'LINK') return 'Shared link';
  return 'Tap page';
};

const replyConsent = (consent: ContactItem['consent']) => {
  if (!consent) return { label: 'Reply consent: no record', tone: 'unknown' };
  switch (consent.status.toUpperCase()) {
    case 'GRANTED': return { label: 'Reply consent: granted', tone: 'granted' };
    case 'WITHDRAWN': return { label: 'Reply consent: withdrawn', tone: 'withdrawn' };
    case 'EXPIRED': return { label: 'Reply consent: expired', tone: 'expired' };
    default: return { label: 'Reply consent: unknown', tone: 'unknown' };
  }
};

function ContactsPage() {
  const { session } = useSession();
  const identity = useRef({ session, version: 0 });
  if (identity.current.session !== session) identity.current = { session, version: identity.current.version + 1 };
  return session ? <ContactsWorkspace key={identity.current.version} /> : null;
}

function ContactsWorkspace() {
  const [configuration, setConfiguration] = useState<ConfigurationResponse | null>(null);
  const [configurationReadError, setConfigurationReadError] = useState(false);
  const [configurationLoading, setConfigurationLoading] = useState(true);
  const [configurationAttempt, setConfigurationAttempt] = useState(0);
  const focusConfigurationAfterRetry = useRef(false);
  const focusExportsAfterRetry = useRef(false);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const exportsGeneration = useRef(0);
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Keep reviewed contacts for comparison while displaying one loaded page.
  const [pageIds, setPageIds] = useState<string[][]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [showAllLoaded, setShowAllLoaded] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const readLocked = useRef(false);
  const currentContacts = useRef<ContactItem[]>([]);
  currentContacts.current = contacts;
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLButtonElement>(null);
  const focusRowAfterNavigation = useRef(false);
  const focusDetailAfterSelection = useRef(false);
  const [activeSearch, setActiveSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replacingContacts, setReplacingContacts] = useState(false);
  const [hasLoadedContacts, setHasLoadedContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [pendingNotesAction, setPendingNotesAction] = useState<(() => void) | null>(null);
  const [notesDialogError, setNotesDialogError] = useState<string | null>(null);
  const notesDialog = useRef<HTMLDialogElement>(null);
  const notesDialogReturnFocus = useRef<HTMLElement | null>(null);
  const [exports, setExports] = useState<ContactExport[]>([]);
  const [exportsError, setExportsError] = useState(false);
  const [exportsLoading, setExportsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const pendingReads = useRef(new Set<string>());
  const [pendingReadCount, setPendingReadCount] = useState(0);
  const contactsGeneration = useRef(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const selected = useMemo(
    () => contacts.find((contact) => contact.id === selectedId) ?? null,
    [contacts, selectedId]
  );

  const notesDirty = selected !== null && notes !== (selected.notes ?? '');

  useEffect(() => {
    const dialog = notesDialog.current;
    if (pendingNotesAction && dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
      if (notesDialogReturnFocus.current?.isConnected) notesDialogReturnFocus.current.focus();
    };
  }, [pendingNotesAction]);

  const reviewUnsavedNotes = (action: () => void) => {
    if (!notesDirty) { action(); return; }
    notesDialogReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setNotesDialogError(null);
    setPendingNotesAction(() => action);
  };

  const loadContacts = useCallback(async (options?: {
    append?: boolean; cursor?: string | null; search?: string; unread?: boolean; failureMessage?: string
  }): Promise<boolean> => {
    if (readLocked.current || saving || pendingReads.current.size > 0) return false;
    readLocked.current = true;
    const generation = ++contactsGeneration.current;
    const search = options?.search ?? activeSearch;
    const unread = options?.unread ?? unreadOnly;
    const params = new URLSearchParams({ take: '25' });
    if (search) params.set('search', search);
    if (unread) params.set('unread', 'true');
    if (options?.cursor) params.set('cursor', options.cursor);
    setLoading(true);
    setReplacingContacts(!options?.append);
    setError(null);
    setPageError(null);
    try {
      const data = await request<ContactsResponse>('/me/contacts?' + params.toString());
      if (!mounted.current || generation !== contactsGeneration.current) return false;
      const ids = data.items.map(item => item.id);
      const existingIds = new Set(currentContacts.current.map(item => item.id));
      if (new Set(ids).size !== ids.length ||
          (options?.append && (ids.some(id => existingIds.has(id)) ||
            (data.nextCursor !== null && data.nextCursor === options.cursor)))) {
        throw new Error('Contacts page could not be verified');
      }
      setContacts(current => options?.append ? [...current, ...data.items] : data.items);
      setPageIds(current => options?.append ? [...current, ids] : [ids]);
      if (options?.append) setPageIndex(pageIds.length);
      else {
        setPageIndex(0);
        setShowAllLoaded(false);
        setSelectedId(null);
      }
      setHasLoadedContacts(true);
      setUnreadCount(data.unreadCount);
      setNextCursor(data.nextCursor);
      return true;
    } catch {
      if (mounted.current && generation === contactsGeneration.current) {
        if (options?.append) setPageError('More contacts could not be loaded. Try again.');
        else setError(options?.failureMessage ?? 'Contacts could not be loaded.');
      }
      return false;
    } finally {
      if (mounted.current && generation === contactsGeneration.current) {
        readLocked.current = false;
        setLoading(false);
        setReplacingContacts(false);
      }
    }
  }, [activeSearch, unreadOnly, pageIds.length, saving]);

  const loadExports = useCallback(async () => {
    const generation = ++exportsGeneration.current;
    setExportsLoading(true);
    try {
      const data = await request<ContactExportsResponse>('/me/contacts/exports');
      if (mounted.current && generation === exportsGeneration.current) {
        setExports(data.items);
        setExportsError(false);
      }
    } catch (reason) {
      if (mounted.current && generation === exportsGeneration.current) setExportsError(true);
      throw reason;
    } finally {
      if (mounted.current && generation === exportsGeneration.current) setExportsLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setConfigurationLoading(true);
    request<ConfigurationResponse>('/me/contacts/configuration').then((data) => {
      if (active) { setConfiguration(data); setConfigurationReadError(false); }
    }).catch(() => {
      if (active) setConfigurationReadError(true);
    }).finally(() => {
      if (active) setConfigurationLoading(false);
    });
    return () => { active = false; };
  }, [configurationAttempt]);

  useEffect(() => {
    void loadExports().catch(() => undefined);
    return () => { exportsGeneration.current += 1; };
  }, [loadExports]);

  useEffect(() => {
    let active = true;
    readLocked.current = true;
    const generation = ++contactsGeneration.current;
    setLoading(true);
    request<ContactsResponse>('/me/contacts?take=25').then((contactsData) => {
      if (!active || generation !== contactsGeneration.current) return;
      const ids = contactsData.items.map(item => item.id);
      if (new Set(ids).size !== ids.length) throw new Error('Contacts page could not be verified');
      setContacts(contactsData.items);
      setPageIds([ids]);
      setPageIndex(0);
      setShowAllLoaded(false);
      setHasLoadedContacts(true);
      setUnreadCount(contactsData.unreadCount);
      setNextCursor(contactsData.nextCursor);
      setSelectedId(null);
    }).catch((reason: { data?: { message?: string } }) => {
      if (active && generation === contactsGeneration.current) setError(reason?.data?.message ?? 'Contacts could not be loaded.');
    }).finally(() => {
      if (active && generation === contactsGeneration.current) { readLocked.current = false; setLoading(false); }
    });
    return () => { active = false; contactsGeneration.current += 1; readLocked.current = false; };
  }, []);

  useLayoutEffect(() => {
    if (configuration && focusConfigurationAfterRetry.current) {
      focusConfigurationAfterRetry.current = false;
      document.getElementById('contact-settings-title')?.focus();
    }
  }, [configuration]);

  useLayoutEffect(() => {
    if (!exportsLoading && !exportsError && focusExportsAfterRetry.current) {
      focusExportsAfterRetry.current = false;
      exportButtonRef.current?.focus();
    }
  }, [exportsLoading, exportsError]);

  useEffect(() => {
    if (!exports.some((item) => item.status === 'QUEUED' || item.status === 'RUNNING')) return;
    const timer = window.setTimeout(() => {
      void loadExports().catch(() => undefined);
    }, 5_000);
    return () => window.clearTimeout(timer);
  }, [exports, loadExports]);

  useEffect(() => {
    setNotes(selected?.notes ?? '');
  }, [selected?.id]);

  const showSelectedDetail = () => {
    detailRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
    detailRef.current?.focus({ preventScroll: true });
  };

  useLayoutEffect(() => {
    if (selectedId && focusDetailAfterSelection.current) {
      focusDetailAfterSelection.current = false;
      showSelectedDetail();
    }
  }, [selectedId]);

  // A different loaded page or filter starts at its first result. Returning to a
  // selected contact below then lets the browser reveal that specific row.
  useLayoutEffect(() => {
    if (resultsRef.current) resultsRef.current.scrollTop = 0;
  }, [pageIndex, showAllLoaded, activeSearch, unreadOnly]);

  useLayoutEffect(() => {
    if (focusRowAfterNavigation.current && selectedRowRef.current) {
      focusRowAfterNavigation.current = false;
      selectedRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      selectedRowRef.current.focus({ preventScroll: true });
    }
  }, [pageIndex, showAllLoaded, selectedId]);

  const returnToSelected = () => {
    if (!selectedId) return;
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      selectedRowRef.current.focus({ preventScroll: true });
      return;
    }
    const target = pageIds.findIndex(page => page.includes(selectedId));
    if (target >= 0) { focusRowAfterNavigation.current = true; setShowAllLoaded(false); setPageIndex(target); }
  };

  const selectContact = (contact: ContactItem) => {
    if (window.matchMedia?.('(max-width: 1100px)').matches) {
      if (selectedId === contact.id) showSelectedDetail();
      else focusDetailAfterSelection.current = true;
    }
    setSelectedId(contact.id);
    if (contact.readAt || pendingReads.current.has(contact.id)) return;
    const generation = contactsGeneration.current;
    setError(null);
    pendingReads.current.add(contact.id);
    setPendingReadCount(pendingReads.current.size);
    void request<{ updated: boolean }>(`/me/contacts/${contact.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ read: true })
    }).then((result) => {
      if (!result?.updated) throw new Error('Read update unconfirmed');
      if (!mounted.current || generation !== contactsGeneration.current) return;
      setContacts((current) => current.map((entry) => entry.id === contact.id
        ? { ...entry, readAt: new Date().toISOString() }
        : entry));
      setUnreadCount((current) => Math.max(0, current - 1));
    }).catch(() => {
      if (mounted.current && generation === contactsGeneration.current) {
        setError('Could not mark this contact as read. Select it again to retry.');
      }
    }).finally(() => {
      pendingReads.current.delete(contact.id);
      if (mounted.current) setPendingReadCount(pendingReads.current.size);
    });
  };

  const updateConfiguration = (field: keyof ContactConfiguration, value: boolean) => {
    setConfiguration((current) => current
      ? {
          ...current,
          configuration: {
            ...current.configuration,
            [field]: value,
            ...(field === 'collectName' && !value ? { requireName: false } : {}),
            ...(field === 'enabled' && !value ? { notifyByEmail: false } : {})
          }
        }
      : current);
  };

  const saveConfiguration = async () => {
    if (!configuration) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await request<Pick<ConfigurationResponse, 'platformAvailable' | 'configuration'>>('/me/contacts/configuration', {
        method: 'PATCH',
        body: JSON.stringify(configuration.configuration)
      });
      setConfiguration((current) => current ? { ...current, ...updated } : current);
      setNotice('Contact form settings saved.');
    } catch (reason: any) {
      setError(reason?.data?.message ?? 'Contact form settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const runSearch = async () => {
    const search = searchInput.trim();
    if (await loadContacts({ search, unread: unreadOnly, failureMessage: 'Contacts could not be searched.' })) {
      setActiveSearch(search);
    }
  };

  const toggleUnread = async () => {
    const next = !unreadOnly;
    if (await loadContacts({ search: activeSearch, unread: next, failureMessage: 'Contacts could not be filtered.' })) {
      setUnreadOnly(next);
    }
  };

  const clearFilters = async () => {
    if (await loadContacts({ search: '', unread: false })) {
      setSearchInput('');
      setActiveSearch('');
      setUnreadOnly(false);
      searchInputRef.current?.focus();
    }
  };

  const nextPage = () => {
    if (loading || saving || readLocked.current || pendingReads.current.size > 0) return;
    if (pageIndex + 1 < pageIds.length) setPageIndex(pageIndex + 1);
    else if (nextCursor) void loadContacts({ append: true, cursor: nextCursor });
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await request(`/me/contacts/${selected.id}`, { method: 'PATCH', body: JSON.stringify({ notes }) });
      setContacts((current) => current.map((contact) => contact.id === selected.id ? { ...contact, notes: notes || null } : contact));
      setNotice('Contact notes saved.');
      return true;
    } catch (reason: any) {
      setError(reason?.data?.message ?? 'Notes could not be saved.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async () => {
    if (!selected || !window.confirm('Delete this contact and its consent receipt? This cannot be undone.')) return;
    setSaving(true);
    try {
      await request(`/me/contacts/${selected.id}`, { method: 'DELETE' });
      contactsGeneration.current += 1;
      const remaining = contacts.filter((contact) => contact.id !== selected.id);
      setContacts(remaining);
      setPageIds(pages => pages.map(page => page.filter(id => id !== selected.id)));
      setSelectedId(remaining[0]?.id ?? null);
      setNotice('Contact details permanently erased.');
    } catch (reason: any) {
      setError(reason?.data?.message ?? 'Contact could not be deleted.');
    } finally {
      setSaving(false);
    }
  };

  const createExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const result = await request<{ created: boolean }>('/me/contacts/exports', { method: 'POST' });
      setNotice(result.created ? 'Contact export queued. It will be available here shortly.' : 'Your contact export is already being prepared.');
      await loadExports().catch(() => undefined);
    } catch (reason: any) {
      setError(reason?.data?.message ?? 'Contact export could not be started.');
    } finally {
      setExporting(false);
    }
  };

  const downloadExport = async (item: ContactExport) => {
    setError(null);
    try {
      const result = await request<{ downloadUrl: string }>(`/me/contacts/exports/${item.id}/download`);
      window.location.assign(result.downloadUrl);
    } catch (reason: any) {
      setError(reason?.data?.message ?? 'Contact export could not be downloaded.');
    }
  };

  const displayedIds = new Set(pageIds[pageIndex] ?? []);
  const visibleContacts = showAllLoaded ? contacts : contacts.filter(contact => displayedIds.has(contact.id));

  return (
    <div className="dashboard-page portal-page contacts-page">
      <header className="portal-intro contacts-page__intro">
        <div>
          <h1>Contacts</h1>
          <p>People who chose to share their details from your tap page.</p>
        </div>
        <div className="contacts-page__tools">
          <span className="contacts-page__unread">{hasLoadedContacts ? unreadCount + ' unread' : loading ? 'Loading contacts...' : 'Unread count unavailable'}</span>
          {configuration ? <a className="contacts-page__settings-link" href="#contact-settings-title">Contact form settings</a> : null}
        </div>
      </header>

      {error ? <div className="alert" role="alert">{error}</div> : null}
      {notice ? <div className="alert success" role="status">{notice}</div> : null}

      <section className="contacts-toolbar" aria-label="Contact search">
        <form onSubmit={(event) => { event.preventDefault(); reviewUnsavedNotes(() => void runSearch()); }}>
          <input ref={searchInputRef} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search contacts" aria-label="Search contacts" aria-describedby="contacts-search-help" />
          <button type="submit" className="button-secondary" disabled={loading || saving || pendingReadCount > 0}>Search</button>
          <span id="contacts-search-help" className="contacts-toolbar__search-help">Search exact email, phone, name, or company.</span>
        </form>
        <button type="button" className={`button-secondary${unreadOnly ? ' is-active' : ''}`} onClick={() => reviewUnsavedNotes(() => void toggleUnread())} disabled={loading || saving || pendingReadCount > 0} aria-pressed={unreadOnly}>{unreadOnly ? 'Showing unread' : 'Unread only'}</button>
        <button ref={exportButtonRef} type="button" className="button-secondary" onClick={() => void createExport()} disabled={exporting}>{exporting ? 'Queuing…' : 'Export CSV'}</button>
      </section>

      {exportsError ? (
        <section className="contacts-exports" aria-label="Recent contact exports" tabIndex={-1}>
          <p role="alert">Export history could not be loaded.</p>
          <button type="button" className="button-secondary" disabled={exportsLoading} onClick={(event) => {
            event.currentTarget.closest('section')?.focus();
            focusExportsAfterRetry.current = true;
            void loadExports().catch(() => undefined);
          }}>{exportsLoading ? 'Loading export history...' : 'Retry export history'}</button>
        </section>
      ) : null}
      {!exportsError && exports.length > 0 ? (
        <section className="contacts-exports" aria-label="Recent contact exports">
          <span className="portal-eyebrow">Recent export</span>
          {exports.slice(0, 1).map((item) => (
            <div key={item.id}>
              <p>
                <strong>{item.status === 'READY' ? `${item.rowCount ?? 0} contacts ready` : item.status === 'FAILED' ? 'Export failed' : item.status === 'EXPIRED' ? 'Export expired' : 'Preparing your CSV…'}</strong>
                <span>Requested {formatDate(item.createdAt)}{item.expiresAt && item.status === 'READY' ? ` · expires ${formatDate(item.expiresAt)}` : ''}</span>
              </p>
              {item.status === 'READY' ? <button type="button" className="button-secondary" onClick={() => void downloadExport(item)}>Download CSV</button> : null}
              {item.status === 'FAILED' ? <button type="button" className="button-secondary" onClick={() => void createExport()}>Try again</button> : null}
            </div>
          ))}
        </section>
      ) : null}

      <div className="contacts-workspace">
        <section className="contacts-list" aria-label="Contact inbox" tabIndex={-1}>
          {loading && contacts.length === 0 ? <p className="helper-text">Loading contacts…</p> : null}
          {!loading && contacts.length === 0 && !nextCursor ? (
            <div className="contacts-empty">
              {!hasLoadedContacts ? (
                <><h2>Contacts unavailable</h2><p>Try loading your contacts again.</p><button type="button" className="button-secondary" onClick={(event) => {
                  event.currentTarget.closest('section')?.focus();
                  void loadContacts();
                }}>Retry contacts</button></>
              ) : activeSearch || unreadOnly ? (
                <><h2>No matching contacts</h2><p>Try a different search or clear your filters.</p><button type="button" className="button-secondary" onClick={() => void clearFilters()}>Clear filters</button></>
              ) : (
                <><h2>No contacts yet</h2><p>When someone deliberately shares their details, they will appear here.</p></>
              )}
            </div>
          ) : null}
          {loading && contacts.length > 0 ? <p className="helper-text" role="status">Loading contacts…</p> : null}
          {pendingReadCount > 0 ? <p className="helper-text" role="status">Updating read status…</p> : null}
          {contacts.length > 0 ? <div className="contacts-list__columns" aria-hidden="true"><span>Name</span><span>Contact details</span><span>Source / reply consent</span></div> : null}
          {visibleContacts.length > 0 ? <>
            <p id="contacts-results-hint" className="contacts-list__scroll-hint">
              Scroll within the contact list to review this page. Use Page Down or the arrow keys when the list is focused.
              Home and End move to the beginning or end of this page.
            </p>
            <div ref={resultsRef} className="contacts-list__scroll" role="region" aria-label="Contact results"
              aria-describedby="contacts-results-hint" tabIndex={0} onKeyDown={handleScrollRegionKeyDown}>
          {visibleContacts.map((contact) => (
            <button key={contact.id} ref={contact.id === selectedId ? selectedRowRef : undefined} type="button" className={`contacts-list__item${contact.id === selectedId ? ' is-selected' : ''}${!contact.readAt ? ' is-unread' : ''}`} aria-pressed={contact.id === selectedId} disabled={loading || saving} onClick={() => selectedId === contact.id ? selectContact(contact) : reviewUnsavedNotes(() => selectContact(contact))}>
              <span className="contacts-list__identity">
                <span className="contacts-list__avatar" aria-hidden="true">{contactInitial(contact)}</span>
                <span className="contacts-list__identity-copy"><strong className="contacts-list__name">{contact.displayName || contact.email || contact.phone || 'Unnamed contact'}</strong><span>{contact.company || 'No company shared'}{!contact.readAt ? ' · Unread' : ''}</span></span>
              </span>
              <span className="contacts-list__contact"><span>{contact.email || 'Email not shared'}</span><span>{contact.phone || 'Phone not shared'}</span></span>
              <span className="contacts-list__source">
                <strong>{sourceLabel(contact.acquisitionSource)}</strong><time dateTime={contact.createdAt}>{formatDate(contact.createdAt)}</time>
                <span className={`contacts-list__consent contacts-list__consent--${replyConsent(contact.consent).tone}`}>{replyConsent(contact.consent).label}</span>
              </span>
            </button>
          ))}
            </div>
          </> : null}
          {hasLoadedContacts && (contacts.length > 0 || nextCursor) ? <>
            {visibleContacts.length === 0 ? <p className="helper-text">No contacts remain on this loaded page.</p> : null}
            {pageError ? <div role="alert" className="contacts-list__page-error"><p>{pageError}</p>
              <button type="button" className="button-secondary" disabled={loading || saving || pendingReadCount > 0} onClick={() => void loadContacts({ append: true, cursor: nextCursor })}>Retry loading contacts</button></div> : null}
            <p className="contacts-list__page-summary" role="status">{showAllLoaded
              ? 'Showing all ' + contacts.length + ' loaded contacts'
              : 'Page ' + (pageIndex + 1) + ' of ' + pageIds.length + ' loaded · ' + visibleContacts.length + ' shown'}</p>
            <nav className="contacts-list__pages" aria-label="Contact pages">
              {!showAllLoaded ? <>
                <button type="button" className="button-secondary" disabled={loading || saving || pendingReadCount > 0 || pageIndex === 0} onClick={() => setPageIndex(0)}>First page</button>
                <button type="button" className="button-secondary" disabled={loading || saving || pendingReadCount > 0 || pageIndex === 0} onClick={() => setPageIndex(pageIndex - 1)}>Previous page</button>
                <button type="button" className="button-secondary" disabled={loading || saving || pendingReadCount > 0 || (pageIndex + 1 >= pageIds.length && !nextCursor)} onClick={nextPage}>Next page</button>
              </> : nextCursor ? <button type="button" className="button-secondary" disabled={loading || saving || pendingReadCount > 0} onClick={() => void loadContacts({ append: true, cursor: nextCursor })}>Load more</button> : null}
              <button type="button" className="button-secondary" disabled={loading || saving || pendingReadCount > 0} onClick={() => setShowAllLoaded(!showAllLoaded)}>{showAllLoaded ? 'Show one page' : 'Show all loaded'}</button>
            </nav>
          </> : null}
        </section>

        <section ref={detailRef} tabIndex={-1} className="contact-detail" aria-label="Selected contact">
          {selected ? (
            <>
              <button type="button" className="button-secondary contact-detail__back" onClick={returnToSelected}>Back to contact list</button>
              <div className="contact-detail__heading">
                <div className="contact-detail__identity">
                  <span className="contact-detail__avatar" aria-hidden="true">{contactInitial(selected)}</span>
                  <div className="contact-detail__identity-copy">
                    <span className="portal-eyebrow">{sourceLabel(selected.acquisitionSource)}</span>
                    <h2>{selected.displayName || 'Unnamed contact'}</h2>
                    <p>{selected.company || `Received ${formatDate(selected.createdAt)}`}</p>
                  </div>
                </div>
                {Math.max(selected.duplicateSignals.emailMatches, selected.duplicateSignals.phoneMatches) > 1 ? <span className="contact-detail__duplicate">Possible duplicate</span> : null}
              </div>
              <dl className="contact-detail__facts">
                <div><dt>Email</dt><dd>{selected.email ? <a href={`mailto:${selected.email}`}>{selected.email}</a> : 'Not shared'}</dd></div>
                <div><dt>Phone</dt><dd>{selected.phone ? <a href={`tel:${selected.phone}`}>{selected.phone}</a> : 'Not shared'}</dd></div>
                <div><dt>Preferred reply</dt><dd>{selected.preferredMethod?.toLowerCase() ?? 'Not specified'}</dd></div>
                <div><dt>Source</dt><dd>{selected.source.cardDisplayId ?? (selected.source.publicSlug ? `Public alias: ${selected.source.publicSlug}` : sourceLabel(selected.acquisitionSource))}</dd></div>
              </dl>
              {selected.message ? <div className="contact-detail__message"><span>Message</span><p>{selected.message}</p></div> : null}
              <label className="contact-detail__notes">Private notes<textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={4000} disabled={replacingContacts} aria-describedby={replacingContacts ? 'contacts-notes-refresh-message' : undefined} /></label>
              {replacingContacts ? <p id="contacts-notes-refresh-message" className="helper-text" role="status">Notes are temporarily unavailable while the contact list updates.</p> : null}
              {notesDirty ? <p className="contact-detail__unsaved" role="status">Unsaved note changes. Leaving Contacts or reloading will discard them.</p> : null}
              <div className="contact-detail__actions">
                <button type="button" className="button-secondary" onClick={() => void saveNotes()} disabled={saving || loading}>Save notes</button>
                <button type="button" className="button-danger" onClick={() => void deleteContact()} disabled={saving || loading}>Erase contact</button>
              </div>
              <p className="contact-detail__consent">{selected.consent
                ? `Reply consent: ${selected.consent.status.toLowerCase()}. Initially recorded ${formatDate(selected.consent.affirmativeAt)}.`
                : 'No reply consent record is available.'}</p>
            </>
          ) : <div className="contacts-empty"><h2>Select a contact</h2><p>Contact details and consent history will appear here.</p></div>}
        </section>
      </div>

      {pendingNotesAction ? <dialog ref={notesDialog} className="contacts-notes-dialog" aria-labelledby="contacts-notes-dialog-title"
        onCancel={(event) => { event.preventDefault(); if (!saving) setPendingNotesAction(null); }}>
        <h2 id="contacts-notes-dialog-title">Save your note changes?</h2>
        <p>Your changes are not saved yet. Save them before continuing, discard them, or return to your note.</p>
        {notesDialogError ? <p role="alert">{notesDialogError}</p> : null}
        <div className="contacts-notes-dialog__actions">
          <button type="button" className="button-secondary" autoFocus disabled={saving} onClick={() => setPendingNotesAction(null)}>Keep editing</button>
          <button type="button" className="button-secondary" disabled={saving} onClick={() => { const action = pendingNotesAction; setPendingNotesAction(null); action(); }}>Discard changes</button>
          <button type="button" className="button-primary" disabled={saving} onClick={() => {
            const action = pendingNotesAction;
            setNotesDialogError(null);
            void saveNotes().then((saved) => {
              if (!mounted.current) return;
              if (saved) { setPendingNotesAction(null); action(); }
              else setNotesDialogError('The save could not be confirmed. Your note is still here; try again or keep editing.');
            });
          }}>{saving ? 'Saving…' : 'Save and continue'}</button>
        </div>
      </dialog> : null}

      {configurationReadError && !configuration ? (
        <section className="contacts-settings" aria-label="Contact form settings" tabIndex={-1}>
          <h2>Contact form unavailable</h2>
          <p role="alert">Contact form settings could not be loaded.</p>
          <button type="button" className="button-secondary" disabled={configurationLoading} onClick={(event) => {
            event.currentTarget.closest('section')?.focus();
            focusConfigurationAfterRetry.current = true;
            setConfigurationAttempt((attempt) => attempt + 1);
          }}>{configurationLoading ? 'Loading contact form...' : 'Retry contact form settings'}</button>
        </section>
      ) : null}
      {configuration ? (
        <section className="contacts-settings" aria-labelledby="contact-settings-title">
          <div className="contacts-settings__heading">
            <div>
              <span className="portal-eyebrow">Contact form</span>
              <h2 id="contact-settings-title" tabIndex={-1}>Let visitors share their details</h2>
              <p>Every submission includes explicit reply consent. Marketing consent is never assumed.</p>
            </div>
            <label className="contacts-settings__master">
              <input
                type="checkbox"
                checked={configuration.configuration.enabled}
                disabled={!configuration.platformAvailable || !configuration.page}
                onChange={(event) => updateConfiguration('enabled', event.target.checked)}
              />
              <span>{configuration.configuration.enabled ? 'On' : 'Off'}</span>
            </label>
          </div>
          {!configuration.platformAvailable ? (
            <p className="contacts-settings__rollout">This account is not in the controlled contact-exchange rollout yet. A super admin can enable it.</p>
          ) : null}
          {!configuration.page ? <p className="contacts-settings__rollout">Create your tap page before turning on the contact form.</p> : null}
          <div className="contacts-settings__fields">
            <label><input type="checkbox" checked={configuration.configuration.collectName} onChange={(event) => updateConfiguration('collectName', event.target.checked)} /> Ask for name</label>
            <label><input type="checkbox" checked={configuration.configuration.requireName} disabled={!configuration.configuration.collectName} onChange={(event) => updateConfiguration('requireName', event.target.checked)} /> Require name</label>
            <label><input type="checkbox" checked={configuration.configuration.collectPhone} onChange={(event) => updateConfiguration('collectPhone', event.target.checked)} /> Allow phone</label>
            <label><input type="checkbox" checked={configuration.configuration.collectCompany} onChange={(event) => updateConfiguration('collectCompany', event.target.checked)} /> Ask for company</label>
            <label><input type="checkbox" checked={configuration.configuration.collectMessage} onChange={(event) => updateConfiguration('collectMessage', event.target.checked)} /> Allow a message</label>
            <label><input type="checkbox" checked={configuration.configuration.notifyByEmail} disabled={!configuration.platformAvailable || !configuration.configuration.enabled} onChange={(event) => updateConfiguration('notifyByEmail', event.target.checked)} /> Email me for new contacts</label>
          </div>
          <p className="contacts-settings__notification-note">Alerts go to your sign-in email, never include visitor details, and are limited to 10 per hour. Every contact still remains in this inbox.</p>
          <button type="button" className="button-secondary" onClick={() => void saveConfiguration()} disabled={saving || !configuration.page}>Save form settings</button>
        </section>
      ) : null}

    </div>
  );
}

export default ContactsPage;
